/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\BasicTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/

import { Megaphone, Phone, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { CallerIdInput } from '../../../components/ui/CallerIdInput';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { MultiSelectButton } from '../../../components/ui/MultiSelectButton';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { useI18n } from '../../../hooks/useI18n';

interface MulticastRoute {
  address: string;
  port: number;
  mode: 'speak' | 'listen';
  interface: string;
  ttl: number;
  codec: string;
  enabled: boolean;
}

interface BasicTabProps {
  formData: {
    code: string;
    name: string;
    mode: string;
    enabled: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    settings: Record<string, any>;
  };
  errors: Record<string, string>;
  availableExtensions: Array<{ id: string; name: string; description?: string; slug?: string }>;
  selectedMemberExtensions: string[];
  handleChange: (field: string, value: string | boolean | number | Record<string, unknown>) => void;
  handleSettingChange: (key: string, value: string | boolean | number | null | Array<unknown>) => void;
  getMulticastRoutes: () => MulticastRoute[];
  addMulticastRoute: () => void;
  updateMulticastRoute: (index: number, field: keyof MulticastRoute, value: MulticastRoute[keyof MulticastRoute]) => void;
  removeMulticastRoute: (index: number) => void;
  onOpenMemberModal: () => void;
  onSubmit: (event?: React.FormEvent) => void;
}

export function BasicTab({
  formData,
  errors,
  availableExtensions,
  selectedMemberExtensions,
  handleChange,
  handleSettingChange,
  getMulticastRoutes,
  addMulticastRoute,
  updateMulticastRoute,
  removeMulticastRoute,
  onOpenMemberModal,
  onSubmit,
}: BasicTabProps) {
  const { t } = useI18n();
  const isMulticast = formData.mode === 'multicast' || formData.mode === 'hybrid';

  return (
    <form onSubmit={onSubmit}>
      <FormBox title={t('pagingGroups.sections.basicSettings')} icon={<Megaphone className="h-5 w-5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <Label htmlFor="code">
            <SimpleTooltip content={t('pagingGroups.fields.code.tooltip')}>
              <span>{t('pagingGroups.fields.code.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="code" type="text" value={formData.code} onChange={(e) => handleChange('code', e.target.value)}
              placeholder={t('pagingGroups.fields.code.placeholder')} className={errors.code ? 'border-red-500' : ''} />
            {errors.code && <p className="text-sm text-red-500 mt-1">{errors.code}</p>}
          </div>

          <Label htmlFor="name">
            <SimpleTooltip content={t('pagingGroups.fields.name.tooltip')}>
              <span>{t('pagingGroups.fields.name.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('pagingGroups.fields.name.placeholder')} className={errors.name ? 'border-red-500' : ''} />
            {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
          </div>

          <Label htmlFor="mode">
            <SimpleTooltip content={t('pagingGroups.fields.mode.tooltip')}>{t('pagingGroups.fields.mode.label')}</SimpleTooltip>
          </Label>
          <div>
            <Select value={formData.mode} onValueChange={(value) => handleChange('mode', value)}>
              <SelectTrigger className={errors.mode ? 'border-red-500' : ''}>
                <SelectValue placeholder={t('pagingGroups.fields.mode.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t('pagingGroups.modes.default')}</SelectItem>
                <SelectItem value="duplex">{t('pagingGroups.modes.duplex')}</SelectItem>
                <SelectItem value="multicast">{t('pagingGroups.modes.multicast')}</SelectItem>
                <SelectItem value="hybrid">{t('pagingGroups.modes.hybrid')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Label htmlFor="timeout">
            <SimpleTooltip content={t('pagingGroups.fields.timeout.tooltip')}>
              <span>{t('pagingGroups.fields.timeout.label')}<span className="text-red-500 ml-1">*</span></span>
            </SimpleTooltip>
          </Label>
          <div>
            <Input id="timeout" type="number" min="5" max="120" value={formData.settings?.timeout ?? 15}
              onChange={(e) => handleSettingChange('timeout', parseInt(e.target.value) || 15)}
              placeholder={t('pagingGroups.fields.timeout.placeholder')} className={errors.timeout ? 'border-red-500' : ''} />
            {errors.timeout && <p className="text-sm text-red-500 mt-1">{errors.timeout}</p>}
          </div>

          <Label htmlFor="callerId">
            <SimpleTooltip content={t('pagingGroups.fields.callerId.tooltip')}>
              <span>{t('pagingGroups.fields.callerId.label')}</span>
            </SimpleTooltip>
          </Label>
          <div>
            <CallerIdInput id="callerId"
              nameValue={formData.settings?.callerIdName || ''} numberValue={formData.settings?.callerIdNumber || ''}
              onNameChange={(value) => handleSettingChange('callerIdName', value)}
              onNumberChange={(value) => handleSettingChange('callerIdNumber', value)}
              namePlaceholder={t('pagingGroups.fields.callerIdName.placeholder')}
              numberPlaceholder={t('pagingGroups.fields.callerIdNumber.placeholder')}
              nameTooltip={t('pagingGroups.fields.callerIdName.tooltip')}
              numberTooltip={t('pagingGroups.fields.callerIdNumber.tooltip')} />
          </div>

          <Label>
            <SimpleTooltip content={t('pagingGroups.fields.enabled.tooltip')}>{t('pagingGroups.fields.enabled.label')}</SimpleTooltip>
          </Label>
          <div>
            <Toggle value={formData.enabled} onChange={(checked) => handleChange('enabled', checked)} />
          </div>

          {isMulticast && (
            <>
              <Label htmlFor="settings.multicastMode">
                <SimpleTooltip content={t('pagingGroups.fields.multicastMode.tooltip')}>{t('pagingGroups.fields.multicastMode.label')}</SimpleTooltip>
              </Label>
              <div>
                <Select value={formData.settings?.multicastMode || 'speak'} onValueChange={(value) => handleSettingChange('multicastMode', value)}>
                  <SelectTrigger><SelectValue placeholder={t('pagingGroups.fields.multicastMode.placeholder')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="speak">{t('pagingGroups.multicastModes.speak')}</SelectItem>
                    <SelectItem value="listen">{t('pagingGroups.multicastModes.listen')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="col-span-4">
            <MultiSelectButton items={availableExtensions} selectedIds={selectedMemberExtensions}
              onClick={onOpenMemberModal} label={t('pagingGroups.fields.members.label')}
              placeholder={t('pagingGroups.fields.members.placeholder')} icon={Phone} maxPreviewItems={3} />
          </div>
        </div>
      </FormBox>

      {isMulticast && (
        <FormBox title={t('pagingGroups.sections.multicastConfiguration')} icon={<Megaphone className="h-5 w-5" />} className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            <div className="col-span-4">
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-lg font-semibold">
                      <SimpleTooltip content={t('pagingGroups.fields.multicastRoutes.tooltip')}>{t('pagingGroups.fields.multicastRoutes.label')}</SimpleTooltip>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">{t('pagingGroups.fields.multicastRoutes.description')}</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addMulticastRoute} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />{t('pagingGroups.actions.addRoute')}
                  </Button>
                </div>
                {errors.multicast_routes && <p className="text-sm text-red-500 mb-2">{errors.multicast_routes}</p>}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '18%' }}>{t('pagingGroups.fields.multicastRoute.address.label')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '10%' }}>{t('pagingGroups.fields.multicastRoute.port.label')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '10%' }}>{t('pagingGroups.fields.multicastRoute.mode.label')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '12%' }}>{t('pagingGroups.fields.multicastRoute.interface.label')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '8%' }}>{t('pagingGroups.fields.multicastRoute.ttl.label')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium" style={{ width: '12%' }}>{t('pagingGroups.fields.multicastRoute.codec.label')}</th>
                        <th className="text-center py-2 px-3 text-sm font-medium" style={{ width: '8%' }}>{t('pagingGroups.fields.multicastRoute.enabled.label')}</th>
                        <th className="text-center py-2 px-3 text-sm font-medium" style={{ width: '10%' }}>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getMulticastRoutes().length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">{t('pagingGroups.messages.noMulticastRoutes')}</td></tr>
                      ) : (
                        getMulticastRoutes().map((route, index) => (
                          <tr key={index} className="border-b border-border hover:bg-muted/50">
                            <td className="py-2 px-3">
                              <Input type="text" value={route.address} onChange={(e) => updateMulticastRoute(index, 'address', e.target.value)}
                                placeholder="239.255.10.10" className={errors[`multicast_route_${index}`] ? 'border-red-500' : ''} />
                              {errors[`multicast_route_${index}`] && <p className="text-xs text-red-500 mt-1">{errors[`multicast_route_${index}`]}</p>}
                            </td>
                            <td className="py-2 px-3">
                              <Input type="number" min={1024} max={65535} value={route.port}
                                onChange={(e) => updateMulticastRoute(index, 'port', parseInt(e.target.value) || 5000)} className="w-full" />
                            </td>
                            <td className="py-2 px-3">
                              <Select value={route.mode} onValueChange={(value) => updateMulticastRoute(index, 'mode', value)}>
                                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="speak">{t('pagingGroups.multicastModes.speak')}</SelectItem>
                                  <SelectItem value="listen">{t('pagingGroups.multicastModes.listen')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2 px-3">
                              <Input type="text" value={route.interface} onChange={(e) => updateMulticastRoute(index, 'interface', e.target.value)}
                                placeholder="eth0" className="w-full" />
                            </td>
                            <td className="py-2 px-3">
                              <Input type="number" min={1} max={10} value={route.ttl}
                                onChange={(e) => updateMulticastRoute(index, 'ttl', parseInt(e.target.value) || 1)} className="w-full" />
                            </td>
                            <td className="py-2 px-3">
                              <Select value={route.codec} onValueChange={(value) => updateMulticastRoute(index, 'codec', value)}>
                                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PCMU">PCMU (�-law)</SelectItem>
                                  <SelectItem value="PCMA">PCMA (A-law)</SelectItem>
                                  <SelectItem value="G722">G722</SelectItem>
                                  <SelectItem value="L16">L16</SelectItem>
                                  <SelectItem value="OPUS">OPUS</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-2 px-3 text-center">
                              <Toggle value={route.enabled} onChange={(checked) => updateMulticastRoute(index, 'enabled', checked)} />
                            </td>
                            <td className="py-2 px-3 text-center">
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeMulticastRoute(index)}
                                className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </FormBox>
      )}
    </form>
  );
}
